import { makeStyles } from "@material-ui/core";

// The checkout-relevant subset of RegionalPlansPage.js's makeStyles, copied
// verbatim (plan Task 1), plus local classes for this page's own elements.
// Parity rule: never re-derive a copied value — re-copy it from the reference.
const useCheckoutClassicStyles = makeStyles((theme) => ({
  // --- Copied reference keys (Step 1) ---
  container: {
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2),
    },
  },
  headerLogo: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(3),
      justifyContent: "center",
    },
  },
  logo: {
    height: 35,
    marginRight: theme.spacing(1),
  },
  partnerSeparator: {
    fontSize: "24px",
    fontWeight: 300,
    color: "#666",
    margin: theme.spacing(0, 1),
    display: "inline-flex",
    alignItems: "center",
  },
  partnerLogo: {
    height: 30,
    marginLeft: theme.spacing(1),
    opacity: 0.9,
  },
  partnershipBadge: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "rgba(16, 185, 129, 0.1)",
    border: "1px solid rgba(16, 185, 129, 0.3)",
    borderRadius: "20px",
    padding: theme.spacing(0.5, 1.5),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      width: "100%",
    },
  },
  badgeText: {
    color: "#10B981",
    fontWeight: 600,
    fontSize: "12px",
    letterSpacing: "0.3px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "11px",
    },
  },
  pageTitle: {
    fontSize: "28px",
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    letterSpacing: "0.2px",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      fontSize: "24px",
      marginBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      marginBottom: theme.spacing(1.5),
    },
  },
  benefitItem: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0),
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  benefitIcon: {
    marginRight: theme.spacing(1),
    fontSize: "16px",
    color: "rgba(4, 207, 102, 0.85)",
    marginTop: "2px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
  },
  benefitTitle: {
    fontSize: "16px",
    fontWeight: 500,
    color: "#222",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    lineHeight: 1.4,
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
    },
  },
  benefitDesc: {
    fontSize: "12px",
    color: "#555",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    lineHeight: 1.4,
    marginTop: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      lineHeight: 1.3,
    },
  },
  formContainer: {
    padding: theme.spacing(3),
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    background: "#ffffff",
    backgroundImage: `radial-gradient(circle at 90% 10%, rgba(4, 207, 102, 0.03) 0%, rgba(255, 255, 255, 0) 70%)`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  formFieldLabel: {
    marginBottom: "6px",
    fontWeight: 500,
    fontSize: "14px",
    color: "#333",
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(0.5),
      fontSize: "14px",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
    },
  },
  formField: {
    marginBottom: theme.spacing(1.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: "0px",
    },
    "& .MuiInputLabel-root": {
      fontWeight: 500,
    },
    "& .MuiOutlinedInput-input": {
      padding: "15px 14px",
      [theme.breakpoints.up("md")]: {
        padding: "12px 14px",
      },
    },
    "&.MuiTextField-root": {
      padding: "0px 4px",
      backgroundColor: "#FFFFFF",
      borderRadius: "4px",
      border: "0.5px solid #e0e0e0",
    },
    [theme.breakpoints.up("md")]: {
      marginBottom: "12px",
    },
  },
  autocompleteInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "0px",
      border: "0.5px solid #e0e0e0",
    },
    "& .MuiAutocomplete-inputRoot": {
      padding: "0px !important",
    },
    "& .MuiAutocomplete-input": {
      padding: "12px 14px !important",
    },
    "& .MuiAutocomplete-popper": {
      maxHeight: "35vh",
    },
    "& .MuiTextField-root": {
      backgroundColor: "#FFFFFF",
      padding: "0px",
    },
  },
  phoneInputContainer: {
    display: "flex",
    alignItems: "center",
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "4px",
      },
    },
    "& .MuiOutlinedInput-adornedStart": {
      paddingLeft: "0px",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "8px",
    },
  },
  countryCodeInput: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f9f9f9",
    borderRadius: "4px 0 0 4px",
    padding: "0 4px",
    height: "100%",
    borderRight: "1px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#f0f9f1",
    },
  },
  planCard: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderRadius: "8px",
    marginBottom: theme.spacing(1.5),
    backgroundColor: "#ffffff",
    border: "1px solid rgba(234, 234, 234, 0.8)",
    position: "relative",
    overflow: "hidden",
    cursor: "pointer",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    // "&:hover": {
    //   transform: "translateY(-2px)",
    //   boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    // },
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "5px",
      height: "100%",
      background: "#04cf66",
      borderTopLeftRadius: "8px",
      borderBottomLeftRadius: "8px",
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
    },
  },
  planHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  planTitle: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#333",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      fontSize: "18px",
    },
  },
  // Last occurrence in reference (line 6261)
  planSubtitle: {
    color: "#555",
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.4,
    fontSize: "14px",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      marginBottom: theme.spacing(1),
    },
  },
  // Last occurrence in reference (line 6273)
  planPriceBottom: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#04cf66",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  featureList: {
    padding: 0,
    marginBottom: theme.spacing(1),
  },
  featureItem: {
    padding: theme.spacing(0.5, 0),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  featureIcon: {
    minWidth: "16px",
    color: "#04cf66",
    marginRight: theme.spacing(0.5),
  },
  featureText: {
    fontSize: "13px",
    lineHeight: 1.4,
    color: "#444",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    margin: 0,
    "& .MuiTypography-root": {
      fontSize: "13px",
      lineHeight: 1.4,
    },
  },
  featureCheck: {
    color: "#04cf66",
    fontSize: "16px",
  },
  // Struck-through list price in a line-item row.
  originalPrice: {
    textDecoration: "line-through",
    color: "#999",
    marginRight: theme.spacing(1),
    fontWeight: 500,
  },
  // "(one-time)" tag after a line item billed once.
  oneTimeTag: {
    color: "#888",
    fontWeight: 400,
  },
  // Billing caption under the plan price.
  billingPeriodText: {
    fontSize: "12px",
    color: "#666",
    fontWeight: 500,
  },
  // "See more features" toggle.
  seeMoreButton: {
    fontSize: "13px",
    color: "#04cf66",
    fontWeight: 500,
    textTransform: "none",
    padding: theme.spacing(0.5, 1),
    backgroundColor: "rgba(4, 207, 102, 0.06)",
    borderRadius: "4px",
    margin: theme.spacing(0.5, 0),
    width: "100%",
    justifyContent: "center",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(4, 207, 102, 0.1)",
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      padding: theme.spacing(0.3, 1),
    },
  },
  passActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  // Last occurrence in reference (line 5859)
  summaryCard: {
    borderRadius: "10px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    backgroundColor: "#ffffff",
    position: "sticky",
    top: "20px",
    border: "1px solid #eaeaea",
    transition: "box-shadow 0.2s",
    overflow: "hidden",
    // "&:hover": {
    //   boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    // },
    "&:after": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      width: "100%",
      height: "4px",
      background: "linear-gradient(to right, #04cf66, #04cf66aa)",
    },
  },
  // Last occurrence in reference (line 5881)
  summaryTitle: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    borderBottom: "2px solid #04cf66",
    paddingBottom: theme.spacing(0.5),
    display: "inline-block",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    letterSpacing: "0.2px",
  },
  // Last occurrence in reference (line 5891)
  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    alignItems: "center",
  },
  // Last occurrence in reference (line 5897)
  summaryItemName: {
    flexGrow: 0.8,
    fontSize: "15px",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    fontWeight: 500,
    color: "#333",
  },
  // Last occurrence in reference (line 5912)
  summaryItemPrice: {
    fontWeight: 600,
    textAlign: "right",
    fontSize: "15px",
    color: "#222",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  // Last occurrence in reference (line 6009)
  proceedButton: {
    padding: theme.spacing(1.25, 0),
    backgroundColor: "#04cf66",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#03b85a",
    },
    marginTop: theme.spacing(1.5),
    borderRadius: "4px",
    fontWeight: 500,
    fontSize: "15px",
    textTransform: "none",
    "&.Mui-disabled": {
      backgroundColor: "#bbbbbb",
      color: "#ffffff",
    },
  },
  mobileProceedButton: {
    backgroundColor: "#04cf66 !important",
    color: "#ffffff !important",
    "&:hover": {
      backgroundColor: "#03b85a !important",
      color: "#ffffff !important",
    },
    "&:focus, &:focus-visible, &:active": {
      backgroundColor: "#03b85a !important",
      color: "#ffffff !important",
      outline: "none",
    },
    fontWeight: 600,
    textTransform: "none",
    "&.Mui-disabled": {
      backgroundColor: "#9adfbe !important",
      color: "#ffffff !important",
      opacity: 1,
    },
    padding: "10px 24px",
    borderRadius: "8px",
    transition: "background-color 0.15s ease, transform 0.15s ease",
    boxShadow: "0 2px 8px rgba(4, 207, 102, 0.3)",
    "&:active": {
      transform: "scale(0.98)",
    },
    width: "auto",
    minWidth: "120px",
  },

  // --- Local classes: this page's own elements, not present in the reference ---
  root: {
    padding: theme.spacing(8),
    background: `linear-gradient(135deg, #f9fcfa 0%, #f5f5f5 100%)`,
    backgroundImage: `
      url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9 0h2v20H9V0zm25.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm-20 20l1.732 1-10 17.32-1.732-1 10-17.32zM58.16 4.134l1 1.732-17.32 10-1-1.732 17.32-10zm-40 40l1 1.732-17.32 10-1-1.732 17.32-10zM80 9v2H60V9h20zm20 69v2H0v-2h20zm79.32-55l-1 1.732-17.32-10L82 4l17.32 10zm-80 80l-1 1.732-17.32-10L2 84l17.32 10zm96.546-75.84l-1.732 1-10-17.32 1.732-1 10 17.32zm-100 100l-1.732 1-10-17.32 1.732-1 10 17.32zM38.16 24.134l1 1.732-17.32 10-1-1.732 17.32-10zM60 29v2H40v-2h20zm19.32 5l-1 1.732-17.32-10L62 24l17.32 10zm16.546 4.16l-1.732 1-10-17.32 1.732-1 10 17.32zM111 40h-2V20h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zM40 49v2H20v-2h20zm19.32 5l-1 1.732-17.32-10L42 44l17.32 10zm16.546 4.16l-1.732 1-10-17.32 1.732-1 10 17.32zM91 60h-2V40h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm24.026 3.294l1 1.732-17.32 10-1-1.732 17.32-10zM39.32 74l-1 1.732-17.32-10L22 64l17.32 10zm16.546 4.16l-1.732 1-10-17.32 1.732-1 10 17.32zM71 80h-2V60h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm24.026 3.294l1 1.732-17.32 10-1-1.732 17.32-10zM120 89v2h-20v-2h20zm-84.134 9.16l-1.732 1-10-17.32 1.732-1 10 17.32zM51 100h-2V80h2v20zm3.134.84l1.732 1-10 17.32-1.732-1 10-17.32zm24.026 3.294l1 1.732-17.32 10-1-1.732 17.32-10zM100 109v2H80v-2h20zm19.32 5l-1 1.732-17.32-10 1-1.732 17.32 10zM31 120h-2v-20h2v20z' fill='%2304cf66' fill-opacity='0.02' fill-rule='evenodd'/%3E%3C/svg%3E"),
      radial-gradient(circle at 25% 10%, rgba(4, 207, 102, 0.05) 0%, rgba(4, 207, 102, 0) 20%),
      radial-gradient(circle at 75% 90%, rgba(4, 207, 102, 0.05) 0%, rgba(4, 207, 102, 0) 20%),
      linear-gradient(135deg, #f9fcfa 0%, #f5f5f5 100%)
    `,
    backgroundAttachment: "fixed",
    position: "relative",
    minHeight: "100vh",
    WebkitOverflowScrolling: "touch",
    overflowY: "auto",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "180px",
      background: `linear-gradient(180deg, rgba(4, 207, 102, 0.08) 0%, rgba(4, 207, 102, 0) 100%)`,
      zIndex: 0,
      pointerEvents: "none", // Prevent pseudo-element from blocking touch events
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 0.5),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(8),
      // Remove fixed background on mobile to fix scroll issues on Android
      backgroundAttachment: "scroll",
    },
  },
  twoColForm: {},
  formTitle: {
    // No fontFamily — inherits the theme's h5 font (Poppins), matching the
    // reference's bare `variant="h5"` title. Forcing Inter here fell back to
    // Roboto (Inter isn't loaded) and rendered narrower than the reference.
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 8,
    width: "100%",
  },
  // Bottom gap for controls that don't carry formField's own marginBottom
  // (the phone row and the custom select) — matches the reference's 12px rhythm.
  fieldBlock: { marginBottom: 12 },
  fieldError: { color: "#D14343", fontSize: 12, marginTop: 4 },
  phoneRow: { display: "flex", gap: 8, alignItems: "stretch" },
  couponRow: { display: "flex", gap: 8, alignItems: "stretch", margin: "8px 0" },
  // Coupon block, copied verbatim from the reference summary.
  couponLabel: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    fontSize: "14px",
    color: "#333",
  },
  couponInput: {
    display: "flex",
    alignItems: "center",
  },
  couponTextField: {
    flexGrow: 1,
    marginRight: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: "4px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
    },
    "&.MuiTextField-root": {
      padding: "0px",
    },
  },
  couponButton: {
    height: "36px",
    padding: "0 12px",
    backgroundColor: "#ffffff",
    color: "#04cf66",
    border: "1px solid #04cf66",
    borderRadius: "4px",
    fontWeight: 500,
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#f0f9f1",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f0f9f1",
      color: "#83d9b2",
      border: "1px solid #c8f0db",
      opacity: 0.8,
    },
  },
  couponApplied: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(1.5),
    padding: "8px 12px",
    background: "rgba(4, 207, 102, 0.08)",
    border: "1px solid #04cf66",
    borderRadius: "4px",
  },
  couponAppliedCode: {
    fontWeight: 600,
    fontSize: "14px",
    color: "#0b6b3a",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  },
  couponRemoveButton: {
    textTransform: "none",
    color: "#666",
    fontWeight: 500,
    minWidth: "auto",
    padding: "0 6px",
  },
  summaryRow: { display: "flex", justifyContent: "space-between", fontSize: 14, padding: "6px 0" },
  // Summary rows copied verbatim from the reference (subtotalRow / totalRow /
  // totalLabel / totalAmount) — the Total sits in a light-green highlight box.
  subtotalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "14px",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    color: "#666",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(1),
    backgroundColor: "rgba(4, 207, 102, 0.05)",
    padding: theme.spacing(1),
    borderRadius: "6px",
  },
  totalLabel: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#222",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  totalAmount: {
    fontSize: "17px",
    fontWeight: 700,
    color: "#04cf66",
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  hr: { border: "none", borderTop: "1px solid #eee", margin: "12px 0" },
  secondaryButton: { textTransform: "none", fontWeight: 600 },
  planBadge: { marginLeft: 8, fontSize: 12, background: "#eafaf1", color: "#0f9d58", padding: "2px 8px", borderRadius: 12 },
  planIncluded: { display: "inline-flex", alignItems: "center", gap: 4, color: "#0f9d58", fontSize: 14, fontWeight: 600 },
  planGst: { fontSize: 12, color: "#888", marginLeft: 6 },
  successCard: { maxWidth: 520, margin: "64px auto", textAlign: "center", padding: "32px 24px", background: "#fff", border: "1px solid #e6e6e6", borderRadius: 12 },
  successIcon: { color: "#04cf66", fontSize: 56 },
  successTitle: { margin: "12px 0 4px", fontSize: 22, color: "#1a1a1a" },
  successSub: { margin: "0 0 20px", color: "#666" },
  successCode: { background: "#f5fdf8", border: "1px dashed #04cf66", borderRadius: 10, padding: 16, margin: "16px 0" },
  successCodeLabel: { fontSize: 13, color: "#666", marginBottom: 4 },
  successCodeValue: { fontSize: 24, fontWeight: 700, letterSpacing: 2, color: "#1a1a1a" },
  successCodeHint: { fontSize: 12, color: "#888", marginTop: 6 },
  successOrder: { fontSize: 13, color: "#888", marginTop: 12 },
  successNextStep: {
    display: "flex", gap: 10, alignItems: "center", textAlign: "left",
    background: "#f0fbf4", border: "1px solid #bbf7d0", borderRadius: 10,
    padding: "13px 14px", margin: "18px 0", fontSize: 13.5, color: "#166534",
  },
  successPrimaryBtn: {
    display: "block", background: "#04cf66", color: "#fff", fontWeight: 600,
    fontSize: 14, borderRadius: 10, padding: 12, textDecoration: "none", marginBottom: 10,
  },

  // --- Mobile sticky CTA bar ---
  mobileStickyBar: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1200,
      backgroundColor: "#ffffff",
      boxShadow: "0 -4px 12px rgba(0,0,0,0.10)",
      padding: theme.spacing(1.25, 2),
      paddingBottom: `calc(${theme.spacing(1.25)}px + env(safe-area-inset-bottom))`,
      backgroundImage:
        "linear-gradient(to right, rgba(4, 207, 102, 0.05), rgba(255, 255, 255, 0.1))",
      "&:before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "2px",
        background:
          "linear-gradient(to right, #04cf66, rgba(4, 207, 102, 0.3))",
      },
    },
  },

  mobileStickyBarHidden: {
    display: "none !important",
  },
  mobileStickyTotalWrap: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.15,
    minWidth: 0,
  },
  mobileStickyTotal: {
    fontWeight: 600,
    fontSize: 18,
    color: "#333333",
  },
  mobileStickyCaption: {
    fontSize: 11,
    color: "#666666",
    marginTop: 2,
  },
  mobileStickyButton: {
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  mobileStickySpacer: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "block",
      height: "calc(24px + env(safe-area-inset-bottom))",
    },
  },
  hideOnMobile: {
    [theme.breakpoints.down("sm")]: {
      display: "none !important",
    },
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 12,
    fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    width: "90%",
    [theme.breakpoints.down("sm")]: {
      fontSize: 24,
      textAlign: "center",
      width: "100%",
    },
  },
  benefitsBlock: {
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
  },
  trustBadge: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  trustIcon: {
    fontSize: "20px",
    color: "#04cf66",
    marginRight: theme.spacing(1),
  },
  trustHeading: {
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: 1.2,
    color: "#333",
    marginBottom: 2,
  },
  trustSubheading: {
    fontSize: "12px",
    lineHeight: 1.2,
    color: "#666",
  },
  trustHighlight: {
    color: "#04cf66",
    fontWeight: 700,
  },
}));

export default useCheckoutClassicStyles;
